(() => {
  if (customElements.get('garan-label')) return;

  class GaranLabel extends HTMLElement {
    constructor() {
      super();
      this.onKeyDown = this.onKeyDown.bind(this);
      this._listenersAbort = null;
      this._previouslyFocused = null;
    }

    connectedCallback() {
      this._listenersAbort?.abort();
      this._listenersAbort = new AbortController();
      const { signal } = this._listenersAbort;

      this.trigger = this.querySelector('[data-garan-trigger]');
      this.dialog = this.dialog || this.querySelector('[data-garan-dialog]');
      this.panel = this.panel || this.dialog?.querySelector('.garan-label__panel');
      this.closeButtons = this.dialog
        ? [...this.dialog.querySelectorAll('[data-garan-close]')]
        : [];

      if (!this.dataset.portalAttached && this.dialog && this.parentNode) {
        this.dataset.portalAttached = 'true';
        document.body.appendChild(this.dialog);
      }

      if (this.trigger) {
        this.trigger.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            this.show();
          },
          { signal },
        );
      }

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.hide(), { signal });
      });
    }

    disconnectedCallback() {
      this._listenersAbort?.abort();
      this._listenersAbort = null;
      document.removeEventListener('keydown', this.onKeyDown);
      document.body.classList.remove('overflow-hidden');
      this.dialog?.remove();
      this.dialog = null;
      this.panel = null;
      delete this.dataset.portalAttached;
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    getFocusableElements() {
      if (!this.panel) return [];
      const selector =
        'a[href]:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return [...this.panel.querySelectorAll(selector)].filter(
        (el) => el.offsetParent !== null || el.getClientRects().length > 0,
      );
    }

    show() {
      if (this.isOpen || !this.dialog) return;

      this._previouslyFocused = document.activeElement;
      this.classList.add('is-open');
      this.dialog.hidden = false;
      this.dialog.classList.add('is-open');
      this.trigger?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeyDown);

      const focusables = this.getFocusableElements();
      const closeBtn = this.panel?.querySelector('.garan-label__close');
      const target = closeBtn && focusables.includes(closeBtn) ? closeBtn : focusables[0];
      target?.focus({ preventScroll: true });
    }

    hide() {
      if (!this.isOpen) return;

      this.classList.remove('is-open');
      this.dialog.hidden = true;
      this.dialog.classList.remove('is-open');
      this.trigger?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeyDown);

      const prev = this._previouslyFocused;
      this._previouslyFocused = null;
      if (prev && typeof prev.focus === 'function') {
        try {
          prev.focus({ preventScroll: true });
        } catch (error) {
          /* ignore */
        }
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.hide();
        return;
      }

      if (event.key !== 'Tab' || !this.panel) return;

      const focusables = this.getFocusableElements();
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first || !this.panel.contains(document.activeElement)) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last || !this.panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('garan-label', GaranLabel);
})();
